import { Geotab } from "@fairfleet/geotab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SpeedLimitReportDetails } from "../models/SpeedLimitReportData";
import { useGeotabClient } from "@/components/GeotabClientContext";
import AddInIds from "@/constants/AddInIds";
import { CastedAddInData } from "@/hooks/addins/useAddinData";

async function updateReport(
  geotabClient: Geotab,
  recordId: string,
  details: SpeedLimitReportDetails,
): Promise<boolean> {
  try {
    await geotabClient.call("Set", {
      typeName: "AddInData",
      entity: { id: recordId, addInId: AddInIds.geotabAddin, details },
    });
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export function useUpdateSpeedLimitReport() {
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();

  const queryKey = ["addinData", AddInIds.geotabAddin, activeSessionId];

  return useMutation({
    mutationFn: async ({
      recordId,
      details,
    }: {
      recordId: string;
      details: SpeedLimitReportDetails;
    }) => updateReport(geotabClient, recordId, details),
    onMutate: async ({ recordId, details }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData =
        queryClient.getQueryData<CastedAddInData<Record<string, unknown>>[]>(queryKey) ?? [];

      const updatedData = previousData.map((item) =>
        item.id === recordId ? { ...item, details: details as unknown as Record<string, unknown> } : item,
      );

      queryClient.setQueryData<CastedAddInData<Record<string, unknown>>[]>(queryKey, updatedData);

      return { previousData };
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData<CastedAddInData<Record<string, unknown>>[]>(
          queryKey,
          context.previousData,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
